import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, CircleCheck, ExternalLink, Eye, Presentation } from "lucide-react";
import { BACKLOG_TOP_DEFAULT } from "@ttc-spectree/toolbox-contract";
import { EngineerNotesBody, SizeDrivers, TextList } from "@/components/ado/grooming-brief-panel";
import { AreaList } from "@/components/grooming/area-list";
import { BriefStateChip } from "@/components/grooming/brief-state-chip";
import { Chip } from "@/components/grooming/grooming-chips";
import { HiddenPointScale, PointScale } from "@/components/grooming/point-scale";
import { isTypingTarget } from "@/components/grooming/use-backlog-shortcuts";
import { useCommandPaletteStore } from "@/components/grooming/use-command-registry";
import { Button } from "@/components/ui/button";
import { useGroomingBacklog } from "@/hooks/queries/use-grooming";
import { useCan } from "@/hooks/queries/use-toolbox-access";
import { useWorkItem, workItemKeys } from "@/hooks/queries/use-work-items";
import { useRefetchOnReturn } from "@/hooks/use-refetch-on-return";
import type { GroomingBacklogItem, GroomingBatch } from "@/lib/api/grooming";
import type { GroomingBrief, WorkItemDetail } from "@/lib/api/work-items";
import { createdShort } from "@/lib/grooming/backlog-sort";
import { FIX_LIST_HEADING, confidenceWords } from "@/lib/grooming/plain-words";
import { getToolboxBridge, toolboxTitleBar, useToolboxBridge } from "@/lib/toolbox/bridge";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * Present mode (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1404; Claude Design
 * "SpecTree Grooming Desktop", `.present` / `.slide` / `.pdots`): the meeting
 * view for screen-sharing in Teams, in SpecTree Toolbox and in a browser.
 *
 * One full-screen slide per agenda item, then a last slide with the items to
 * fix before the next meeting. → / Space and ← move, S reveals, T folds the
 * technical details, O opens the item in ADO, Esc exits.
 *
 * The item slide (epic PI-SPEC-SW-E303, ER-1656, frames K1-K3) shows the same
 * plain brief as the side pane in a left column that scrolls with the wheel:
 * What is it, Who notices, What gets touched, Worth knowing and To settle in
 * the meeting (older briefs without a plain brief keep the summary and the
 * questions). Beside the item ID the header says "Written by <name> on <date>"
 * ("Written on <date>" without a creator). Below the plain brief, Technical
 * details (In short, What changes, engineer notes) are open on every slide; T
 * or a click on the heading folds them for that slide only, and the next slide
 * opens them again. The right column holds the Team's size card and Claude's
 * size and stays put while the left column scrolls. Every size-leaning field
 * (size in words, comparable in words, Why this size, Similar past work, the
 * split suggestion) is in the right column and absent from the DOM until S.
 *
 * Claude's suggested size stays hidden until the team has sized the item
 * (requester decision 4: no anchoring). Hidden means absent: until S is
 * pressed the slide renders placeholder cells and no number, so nobody on the
 * call can read the suggestion through a failed blur or the dev tools.
 * Reveals are per slide and live in this component, so they reset every time
 * Present mode closes.
 *
 * In a browser it also asks for full screen (when allowed); in SpecTree
 * Toolbox the overlay covers the window and keeps clear of the native window
 * controls the app reports.
 *
 * Recording the team's size (UX-PM-05, frame C1, feature PI-SPEC-SW-1488):
 * each item slide has Open in ADO (button and O) beside the item ID, which
 * opens the item in the browser through the ordinary external link, where a
 * person types the agreed Story Points. When the presenter comes back to the
 * window the slide re-reads the item and its Team's size card shows the
 * points ADO now has. The Toolbox never writes Story Points (01-roles.md
 * §1.1), and Claude's size stays hidden until S even while the team's points
 * show (01-roles.md §9.1, 2026-10-14).
 *
 * Who may present (epic PI-SPEC-SW-E283): the `present` capability on the
 * project (Admin, Project Manager, Product Owner). Present mode has no API
 * action of its own, so `GET /toolbox/access` is its only gate: the entry
 * points (button, P key, palette, `?present=1`) hide without it, and
 * `AgendaPresent` renders nothing.
 */

// ---------------------------------------------------------------------------
// Slides from an agenda
// ---------------------------------------------------------------------------

/** One agenda item to present. */
export interface PresentItem {
  itemId: string;
  title: string;
}

/** One item that does not meet the grooming bar yet, and why. */
export interface PresentFix {
  itemId: string;
  title: string;
  why: string;
}

/** At most this many items are listed on the fix list slide (FIX_LIST_HEADING). */
export const PRESENT_FIX_LIMIT = 10;

/** The agenda's items in discussion order, without those below the grooming bar. */
export function presentItems(batch: Pick<GroomingBatch, "items"> | null): PresentItem[] {
  if (!batch) return [];
  return [...batch.items]
    .sort((a, b) => a.position - b.position)
    .filter((item) => item.status !== "not-ready")
    .map((item) => ({ itemId: item.itemId, title: item.title }));
}

/** "Missing: Repro steps, Severity", from a backlog row's readiness. */
export function missingWhy(row: Pick<GroomingBacklogItem, "readiness">): string {
  const labels = row.readiness.missing.map((missing) => missing.label).filter(Boolean);
  return labels.length > 0 ? `Missing: ${labels.join(", ")}` : "Missing information";
}

/**
 * The fix list (FIX_LIST_HEADING): agenda items the run found below
 * the bar (with the engine's reason), then items near the top of the backlog
 * (the first {@link BACKLOG_TOP_DEFAULT}, in backlog order) that still need
 * information. At most {@link PRESENT_FIX_LIMIT}.
 */
export function presentFixes(
  batch: Pick<GroomingBatch, "items"> | null,
  backlogItems: readonly GroomingBacklogItem[] | undefined
): PresentFix[] {
  const fixes = new Map<string, PresentFix>();
  for (const item of [...(batch?.items ?? [])].sort((a, b) => a.position - b.position)) {
    if (item.status !== "not-ready") continue;
    fixes.set(item.itemId, { itemId: item.itemId, title: item.title, why: item.errorMessage ?? "Missing information" });
  }
  for (const row of (backlogItems ?? []).slice(0, BACKLOG_TOP_DEFAULT)) {
    if (row.stage !== "needs-information" || fixes.has(row.itemId)) continue;
    fixes.set(row.itemId, { itemId: row.itemId, title: row.title, why: missingWhy(row) });
  }
  return [...fixes.values()].slice(0, PRESENT_FIX_LIMIT);
}

/** `2026-10-15` → "Thursday, Oct 15". */
function formatDate(date: string): string {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

/** "Claude suggests 5 · range 3–8 · Fairly sure": confidence in the shared words (UX-PM-09). */
export function suggestionText(brief: Pick<GroomingBrief, "suggestedPoints" | "pointsLow" | "pointsHigh" | "confidence">): string {
  const range =
    brief.pointsLow === brief.pointsHigh
      ? `range ${String(brief.pointsLow)}`
      : `range ${String(brief.pointsLow)}–${String(brief.pointsHigh)}`;
  return `Claude suggests ${String(brief.suggestedPoints)} · ${range} · ${confidenceWords(brief.confidence)}`;
}

/**
 * The team's points: the item's live ADO estimate when it is a finite
 * positive number, else null. The backlog's rule (groomingBacklogService
 * `teamPoints = finitePositive(item.estimate)`). Pure; exported for tests.
 */
export function teamPointsOf(detail: Pick<WorkItemDetail, "estimate"> | undefined): number | null {
  const estimate = detail?.estimate;
  return typeof estimate === "number" && Number.isFinite(estimate) && estimate > 0 ? estimate : null;
}

/** "Team: 5 points", "Team: 1 point". */
export function teamPointsText(points: number): string {
  return `Team: ${String(points)} ${points === 1 ? "point" : "points"}`;
}

/** A read time, "10:42 AM". */
function formatReadTime(at: number): string {
  return new Date(at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/**
 * A slide re-reads its item when it opens if the last read is older than this,
 * so a slide never shows points from long before (the app-wide staleTime is 60 s).
 */
export const PRESENT_ITEM_STALE_MS = 30_000;

// ---------------------------------------------------------------------------
// The slides
// ---------------------------------------------------------------------------

const EYEBROW = "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";

function Kbd({ children }: { children: string }) {
  return (
    <kbd className={cn("whitespace-nowrap rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 py-px font-sans text-[11px] font-medium", TOOLBOX_TEXT.faint)}>
      {children}
    </kbd>
  );
}

/**
 * The Team's size card (frame C1): the points the team typed in ADO, read
 * back from the item, or "No points in ADO yet". Read only; the Toolbox never
 * writes Story Points (01-roles.md §1.1). While a re-read runs it keeps the
 * last value, so the card does not flicker.
 */
function TeamSizeCard({
  detail,
  readAt,
  readOnReturn,
  failed,
}: {
  detail: WorkItemDetail;
  /** When the item was last read (ms). */
  readAt: number;
  /** True when that read followed the presenter coming back to the window. */
  readOnReturn: boolean;
  /** True when the latest re-read failed (the card keeps the last value). */
  failed: boolean;
}) {
  const points = teamPointsOf(detail);
  const time = formatReadTime(readAt);
  const readLine = failed
    ? "The points could not be read from ADO just now."
    : readOnReturn
      ? `Read back from Azure DevOps when you came back to this window, ${time}.`
      : `Read from Azure DevOps at ${time}.`;
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-[10px] border px-5",
        points !== null ? "border-[rgba(34,197,94,0.4)] bg-[#0C1510] py-[18px]" : "border-[#1F1F23] bg-[#111113] py-4"
      )}
      data-testid="present-team-size"
      data-state={points !== null ? "recorded" : "none"}
    >
      <span className={cn(EYEBROW, points !== null && "text-[#86EFAC]")}>Team&apos;s size</span>
      {points !== null ? (
        <div className="flex flex-wrap items-center gap-3">
          <CircleCheck className="h-[26px] w-[26px] flex-none text-green-400" aria-hidden="true" />
          <span className="font-display text-[26px] font-bold leading-none" data-testid="present-team-points">
            {teamPointsText(points)}
          </span>
          <Chip tone="ok">recorded in ADO</Chip>
        </div>
      ) : (
        <span className="text-base text-muted-foreground" data-testid="present-team-points">
          No points in ADO yet · Open in ADO to type them
        </span>
      )}
      <span className="text-[13px] leading-normal text-muted-foreground" data-testid="present-team-read" role={failed ? "alert" : undefined}>
        {readLine} The Toolbox never changes points; they are typed in ADO.
      </span>
    </div>
  );
}

/**
 * Claude's size, hidden until S, then the scale, the suggestion and every other
 * field that leans toward the size: size in words, Why this size, Similar past
 * work and the split suggestion. Before S none of them is rendered at all.
 */
function SizeColumn({ brief, revealed, onReveal }: { brief: GroomingBrief; revealed: boolean; onReveal: () => void }) {
  const plain = brief.body.plainBrief;
  const { sizeDrivers, comparables, splitSuggestion } = brief.body;
  const sizeWords = [plain?.sizeInWords, plain?.comparableInWords].filter(Boolean).join(" ");
  return (
    <div className="flex flex-col gap-4" data-testid="present-size">
      {revealed ? (
        <>
          <PointScale size="xl" low={brief.pointsLow} high={brief.pointsHigh} pick={brief.suggestedPoints} />
          <span className="text-base text-muted-foreground motion-safe:animate-slide-up" data-testid="present-suggestion">
            {suggestionText(brief)}
          </span>
          {sizeWords !== "" && (
            <p className="text-base leading-normal text-zinc-300" data-testid="present-size-words">
              {sizeWords}
            </p>
          )}
          {sizeDrivers.length > 0 && (
            <section className="flex flex-col gap-2" data-testid="present-size-drivers">
              <span className={EYEBROW}>Why this size</span>
              <SizeDrivers drivers={sizeDrivers} />
            </section>
          )}
          {brief.calibrated && comparables.length > 0 && (
            <section className="flex flex-col gap-2" data-testid="present-comparables">
              <span className={EYEBROW}>Similar past work</span>
              <ul className="space-y-1 text-sm">
                {comparables.map((c) => (
                  <li key={c.id}>
                    <span className="font-mono text-xs text-muted-foreground">#{c.id}</span> <span className="font-medium">{c.title}</span>{" "}
                    <span className="text-muted-foreground">({c.points} pts)</span> — {c.why}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {splitSuggestion && (
            <p className="text-sm" data-testid="present-split">
              <span className="font-medium">Consider splitting:</span> {splitSuggestion}
            </p>
          )}
        </>
      ) : (
        <>
          <HiddenPointScale size="xl" />
          <div className="flex flex-col gap-2.5">
            <span className="text-base leading-normal text-muted-foreground">
              Claude&apos;s suggestion is hidden so the team sizes it first.
            </span>
            <Button
              type="button"
              variant="outline"
              className="h-9 self-start"
              onClick={onReveal}
              data-testid="present-reveal"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Reveal suggestion
              <Kbd>S</Kbd>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** "Written by Chris Patel on Oct 9", "Written on Oct 9" without a creator, or "" with neither. */
export function writtenByText(creator: string | undefined, date: string | undefined): string {
  const when = createdShort(date);
  if (creator) return `Written by ${creator}${when ? ` on ${when}` : ""}`;
  return when ? `Written on ${when}` : "";
}

/** A labelled block of the plain brief. */
function SlideSection({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <span className={EYEBROW}>{title}</span>
      {children}
    </section>
  );
}

/**
 * Technical details: open by default, folded by T or a click on the heading.
 * Never holds a size-leaning field (those wait for S in the size column).
 */
function TechnicalPanel({ brief, folded, onToggle }: { brief: GroomingBrief; folded: boolean; onToggle: () => void }) {
  const body = brief.body;
  return (
    <section className="flex flex-col gap-3 rounded-[10px] border border-[#1F1F23] bg-[#0E0E10] px-5 py-4" data-testid="present-technical" data-state={folded ? "folded" : "open"}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!folded}
        className="flex items-center gap-2 text-left"
        data-testid="present-technical-toggle"
      >
        {folded ? <ChevronRight className="h-4 w-4 flex-none" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 flex-none" aria-hidden="true" />}
        <span className={EYEBROW}>Technical details</span>
        <span className="flex-1" />
        <Kbd>T</Kbd>
      </button>
      {!folded && (
        <div className="flex flex-col gap-4" data-testid="present-technical-body">
          <section className="flex flex-col gap-1.5">
            <h3 className={EYEBROW}>In short</h3>
            <p className="whitespace-pre-line text-[15px] leading-normal">{body.plainSummary}</p>
          </section>
          {body.whatChanges.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <h3 className={EYEBROW}>What changes</h3>
              <TextList items={body.whatChanges} />
            </section>
          )}
          <EngineerNotesBody brief={brief} listTextClassName="text-[13px]" />
        </div>
      )}
    </section>
  );
}

/** One agenda item's slide. Reads the item (and its latest brief) through the item query. */
function ItemSlide({
  project,
  item,
  revealed,
  onReveal,
  onOpenInAdo,
  returnedAt,
  toggleTechnicalRef,
}: {
  project: string;
  item: PresentItem;
  revealed: boolean;
  onReveal: () => void;
  /** Filled with this slide's fold toggle, so the T key can reach it. */
  toggleTechnicalRef: RefObject<(() => void) | null>;
  /** Called before the item opens in ADO (the button or O), so leaving full screen for the new tab keeps Present open. */
  onOpenInAdo: () => void;
  /** When the presenter last came back to the window on this slide, or null. */
  returnedAt: number | null;
}) {
  const detail = useWorkItem("ado", project, item.itemId, { staleTime: PRESENT_ITEM_STALE_MS });
  const adoUrl = detail.data?.ref.url;
  const brief = detail.data?.groomingBrief?.brief ?? null;
  const plain = brief?.body.plainBrief;
  const what = brief ? (plain?.whatIsIt ?? brief.body.plainSummary) : "";
  // Local state: the slide is keyed by its index, so the next slide opens its details again.
  const [technicalFolded, setTechnicalFolded] = useState(false);
  const toggleTechnical = useCallback(() => {
    setTechnicalFolded((folded) => !folded);
  }, []);
  useEffect(() => {
    const ref = toggleTechnicalRef;
    ref.current = toggleTechnical;
    return () => {
      ref.current = null;
    };
  }, [toggleTechnicalRef, toggleTechnical]);
  const written = writtenByText(detail.data?.createdBy?.displayName, detail.data?.createdDate);
  const askTheTeam = brief?.body.plainBrief?.askTheTeam;
  const questions = brief ? (askTheTeam && askTheTeam.length > 0 ? askTheTeam : brief.body.questions) : [];
  const typeName = detail.data?.typeName;
  // UX-PM-09: an out-of-date brief says so on its slide. The chip carries no
  // number, so it never reveals Claude's size (01-roles.md §9.1, 2026-10-14).
  const stale = brief !== null && detail.data?.groomingBrief?.stale === true;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-12 overflow-auto px-8 pb-6 pt-9 lg:flex-row lg:overflow-hidden lg:px-16" data-testid="present-item-slide" data-item-id={item.itemId}>
      <div className="flex w-full min-w-0 shrink-0 flex-col gap-5 lg:min-h-0 lg:w-[640px] lg:flex-none lg:overflow-y-auto lg:pr-4" data-testid="present-left-column">
        <span className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-sm text-muted-foreground" data-testid="present-item-id">
            AB#{item.itemId}
            {typeName ? ` · ${typeName}` : ""}
            {written ? ` · ${written}` : ""}
          </span>
          {stale && <BriefStateChip state="out-of-date" testId="present-brief-state" />}
          {adoUrl && (
            <Button asChild variant="outline" className="h-8 gap-1.5 px-2.5 text-[13px]">
              {/* The ordinary external link: a browser opens a tab, SpecTree Toolbox the default browser. */}
              <a href={adoUrl} target="_blank" rel="noreferrer" onClick={onOpenInAdo} data-testid="present-open-ado">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Open in ADO
                <Kbd>O</Kbd>
              </a>
            </Button>
          )}
        </span>
        <h2 className="font-display text-[38px] font-semibold leading-[1.15] tracking-[-0.01em]" data-testid="present-item-title">
          {detail.data?.title ?? item.title}
        </h2>
        {detail.isLoading ? (
          <span className="text-lg text-muted-foreground" role="status">
            Loading the brief…
          </span>
        ) : detail.isError && !detail.data ? (
          <span className="text-lg text-muted-foreground" role="alert">
            The brief could not be loaded. Try again in a moment.
          </span>
        ) : !brief ? (
          <span className="text-lg text-muted-foreground" data-testid="present-no-brief">
            The brief for this item isn&apos;t ready yet.
          </span>
        ) : (
          <>
            <p className="max-w-[640px] text-[19px] leading-[1.55] text-zinc-300" data-testid="present-what">
              {what}
            </p>
            {plain?.whoNotices && (
              <SlideSection title="Who notices" testId="present-who-notices">
                <p className="text-[17px] leading-normal text-zinc-300">{plain.whoNotices}</p>
              </SlideSection>
            )}
            {plain && plain.areasTouched.length > 0 && (
              <SlideSection title="What gets touched" testId="present-areas">
                <AreaList areas={plain.areasTouched} />
              </SlideSection>
            )}
            {plain && plain.worthKnowing.length > 0 && (
              <SlideSection title="Worth knowing" testId="present-worth-knowing">
                <TextList items={plain.worthKnowing} />
              </SlideSection>
            )}
            {questions.length > 0 && (
              <div className="mt-2 flex flex-col gap-2.5">
                <span className={EYEBROW}>To settle in the meeting</span>
                <ul className="flex list-disc flex-col gap-2.5 pl-[18px] text-lg leading-normal" data-testid="present-questions">
                  {questions.map((question) => (
                    <li key={question}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
            <TechnicalPanel brief={brief} folded={technicalFolded} onToggle={toggleTechnical} />
          </>
        )}
      </div>
      {detail.data && (
        <div className="flex w-full min-w-0 shrink-0 flex-col gap-4 lg:min-h-0 lg:max-w-[560px] lg:flex-1 lg:shrink lg:overflow-y-auto" data-testid="present-size-column">
          <TeamSizeCard
            detail={detail.data}
            readAt={detail.dataUpdatedAt}
            readOnReturn={returnedAt !== null && detail.dataUpdatedAt >= returnedAt}
            failed={detail.isError}
          />
          {brief && <SizeColumn brief={brief} revealed={revealed} onReveal={onReveal} />}
        </div>
      )}
    </div>
  );
}

/** The last slide: items that don't meet the grooming bar yet. */
function FixSlide({ fixes }: { fixes: readonly PresentFix[] }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-6 pt-9 lg:px-16" data-testid="present-fix-slide">
      <h2 className="font-display text-[34px] font-semibold leading-tight tracking-[-0.01em]">{FIX_LIST_HEADING}</h2>
      {fixes.length === 0 ? (
        <p className="text-[17px] text-muted-foreground" data-testid="present-no-fixes">
          Nothing to fix: the items near the top of the backlog meet the grooming bar.
        </p>
      ) : (
        <>
          <p className="text-[17px] text-muted-foreground">
            These don&apos;t meet the grooming bar yet, so they weren&apos;t on the agenda.
          </p>
          <ul className="glass-card max-w-[900px] overflow-hidden rounded-[10px]" aria-label={FIX_LIST_HEADING}>
            {fixes.map((fix) => (
              <li
                key={fix.itemId}
                className="flex flex-wrap items-center gap-3 border-b border-[#141417] px-5 py-3.5 text-base last:border-b-0"
                data-testid="present-fix-row"
                data-item-id={fix.itemId}
              >
                <span className="font-mono text-xs text-muted-foreground">AB#{fix.itemId}</span>
                <span className="min-w-0 flex-1">{fix.title}</span>
                <span className="text-muted-foreground">{fix.why}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Warm the next item's read so the next slide opens without a wait. */
function Prefetch({ project, itemId }: { project: string; itemId: string }) {
  useWorkItem("ado", project, itemId);
  return null;
}

// ---------------------------------------------------------------------------
// Full screen (browser only)
// ---------------------------------------------------------------------------

/**
 * How long after Open in ADO a leave of full screen is taken as the browser's
 * doing (opening the new tab), not the presenter's.
 */
export const OPEN_IN_ADO_LEAVE_GRACE_MS = 3000;

/**
 * In a browser, ask for full screen while Present mode is open and close
 * Present mode when the person leaves full screen (the browser takes Esc for
 * itself there). Inside SpecTree Toolbox the overlay already covers the window.
 *
 * Opening a new tab can take the page out of full screen. Call the returned
 * `expectLeave` just before Open in ADO: the one leave that follows within
 * {@link OPEN_IN_ADO_LEAVE_GRACE_MS} keeps Present mode open.
 */
function useBrowserFullscreen(onLeave: () => void): { expectLeave: () => void } {
  const leave = useRef(onLeave);
  useEffect(() => {
    leave.current = onLeave;
  }, [onLeave]);
  const expectedUntil = useRef(0);
  const expectLeave = useCallback(() => {
    expectedUntil.current = Date.now() + OPEN_IN_ADO_LEAVE_GRACE_MS;
  }, []);

  useEffect(() => {
    if (getToolboxBridge() !== null) return undefined;
    const root = document.documentElement;
    if (!document.fullscreenEnabled || typeof root.requestFullscreen !== "function") return undefined;
    let entered = false;
    const onChange = () => {
      if (document.fullscreenElement) {
        entered = true;
      } else if (entered) {
        entered = false;
        if (Date.now() <= expectedUntil.current) {
          // Left for the ADO tab, not by the presenter: stay open (the overlay still covers the page).
          expectedUntil.current = 0;
          return;
        }
        leave.current();
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    if (!document.fullscreenElement) {
      // Refused without a click (a deep link) or by policy: the overlay still covers the page.
      root.requestFullscreen().catch(() => undefined);
    }
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement && typeof document.exitFullscreen === "function") {
        document.exitFullscreen().catch(() => undefined);
      }
    };
  }, []);

  return { expectLeave };
}

// ---------------------------------------------------------------------------
// Present mode
// ---------------------------------------------------------------------------

/** The action a key press asks for in Present mode, or null. Pure; exported for tests. */
export function presentKeyAction(key: string): "next" | "previous" | "reveal" | "technical" | "open" | "exit" | null {
  switch (key) {
    case "ArrowRight":
    case " ":
    case "Spacebar":
      return "next";
    case "ArrowLeft":
      return "previous";
    case "s":
    case "S":
      return "reveal";
    case "t":
    case "T":
      return "technical";
    case "o":
    case "O":
      return "open";
    case "Escape":
      return "exit";
    default:
      return null;
  }
}

/**
 * The footer's key hint. An item slide offers "O to open in ADO", and
 * "T for technical details", and "S to reveal" only while its size can still
 * be revealed (a brief, not yet revealed):
 * "← → to move · O to open in ADO · T for technical details · S to reveal". The
 * closing slide keeps "← → to move · share this window in Teams" (UX-PM-35).
 * Pure; exported for tests.
 */
export function presentFooterText(slide: { onItem: boolean; canReveal: boolean }): string {
  if (!slide.onItem) return "← → to move · share this window in Teams";
  return slide.canReveal
    ? "← → to move · O to open in ADO · T for technical details · S to reveal"
    : "← → to move · O to open in ADO · T for technical details";
}

export interface PresentModeProps {
  project: string;
  /** `YYYY-MM-DD` for the top bar, or null. */
  sessionDate: string | null;
  items: readonly PresentItem[];
  fixes: readonly PresentFix[];
  onClose: () => void;
}

/** The full-screen overlay. Mount it to open Present mode and unmount it to close (that resets every reveal). */
export function PresentMode({ project, sessionDate, items, fixes, onClose }: PresentModeProps) {
  const bridge = useToolboxBridge();
  const titleBar = toolboxTitleBar(bridge);
  const rootRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState<Readonly<Record<string, boolean>>>({});
  const slideCount = items.length + 1;
  const current = index < items.length ? items[index] : undefined;
  const next = index + 1 < items.length ? items[index + 1] : undefined;
  // The current item's read (shared with its slide): the footer offers S only while a size can still be revealed.
  const currentDetail = useWorkItem("ado", project, current?.itemId);
  const canReveal = current !== undefined && Boolean(currentDetail.data?.groomingBrief?.brief) && revealed[current.itemId] !== true;

  const move = useCallback(
    (step: number) => {
      setIndex((at) => Math.max(0, Math.min(slideCount - 1, at + step)));
    },
    [slideCount]
  );
  const toggleTechnicalRef = useRef<(() => void) | null>(null);
  const reveal = useCallback(() => {
    if (!current) return;
    setRevealed((state) => ({ ...state, [current.itemId]: true }));
  }, [current]);

  const { expectLeave } = useBrowserFullscreen(onClose);

  // Coming back to the window (after typing the points in ADO) re-reads the
  // current slide's item; the slide, the footer and the run's Agenda view share that read.
  const queryClient = useQueryClient();
  const [returned, setReturned] = useState<{ itemId: string; at: number } | null>(null);
  useRefetchOnReturn(() => {
    if (!current) return;
    setReturned({ itemId: current.itemId, at: Date.now() });
    void queryClient.invalidateQueries({ queryKey: workItemKeys.detail("ado", project, current.itemId), exact: true });
  }, current !== undefined);

  /** O: follow the current slide's Open in ADO link (its click handler expects the full-screen leave). */
  const openInAdo = useCallback(() => {
    rootRef.current?.querySelector<HTMLAnchorElement>('[data-testid="present-open-ado"]')?.click();
  }, []);

  // Keyboard: → / Space next, ← previous, S reveal, T technical details, O open in ADO, Esc exit.
  const toggleTechnical = useCallback(() => {
    toggleTechnicalRef.current?.();
  }, []);
  const actions = useRef({ move, reveal, toggleTechnical, openInAdo, onClose });
  useEffect(() => {
    actions.current = { move, reveal, toggleTechnical, openInAdo, onClose };
  }, [move, reveal, toggleTechnical, openInAdo, onClose]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (useCommandPaletteStore.getState().open || isTypingTarget(event.target)) return;
      const action = presentKeyAction(event.key);
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      if (action === "next") actions.current.move(1);
      else if (action === "previous") actions.current.move(-1);
      else if (action === "reveal") actions.current.reveal();
      else if (action === "technical") actions.current.toggleTechnical();
      // A held O opens one tab, not one per key repeat.
      else if (action === "open") {
        if (!event.repeat) actions.current.openInAdo();
      } else actions.current.onClose();
    };
    // Capture: Present mode owns these keys over the page underneath it.
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  // Take focus from the page (so Space never presses a button behind) and give it back on close.
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    rootRef.current?.focus();
    return () => {
      previous?.focus();
    };
  }, []);

  // Keep clear of the app's native window controls (contract 2), like the Toolbox title bar.
  const reservedLeft = titleBar?.reservedLeft ?? 0;
  const reservedRight = titleBar?.reservedRight ?? 0;
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.setProperty("--present-reserved-left", `${String(reservedLeft)}px`);
    bar.style.setProperty("--present-reserved-right", `${String(reservedRight)}px`);
  }, [reservedLeft, reservedRight]);

  const heading = ["Grooming", project, sessionDate ? formatDate(sessionDate) : null].filter(Boolean).join(" · ");

  return createPortal(
    <div
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Present the agenda"
      className="fixed inset-0 z-[80] flex flex-col bg-[#070708] text-foreground outline-none motion-safe:animate-in motion-safe:fade-in-0"
      data-testid="present-mode"
    >
      <div
        ref={barRef}
        className={cn(
          "flex min-h-[42px] flex-none items-center gap-3 border-b border-[#18181B] py-3.5 pl-[calc(24px+var(--present-reserved-left,0px))] pr-[calc(24px+var(--present-reserved-right,0px))]",
          titleBar && "[-webkit-app-region:drag]"
        )}
        data-testid="present-top-bar"
      >
        <span className="h-2 w-2 flex-none rounded-[2px] bg-[#E11837]" aria-hidden="true" />
        <span className={cn(EYEBROW, "min-w-0 flex-1 truncate")} data-testid="present-heading">
          {heading}
        </span>
        <span className="text-xs text-muted-foreground" data-testid="present-position" aria-live="polite">
          {current ? `${String(index + 1)} of ${String(items.length)}` : "After the agenda"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-lg px-2.5 font-medium text-muted-foreground hover:bg-[#141417] hover:text-foreground [-webkit-app-region:no-drag]"
          data-testid="present-exit"
        >
          Exit
          <Kbd>Esc</Kbd>
        </button>
      </div>

      <div key={index} className="flex min-h-0 flex-1 motion-safe:animate-slide-up">
        {current ? (
          <ItemSlide
            project={project}
            item={current}
            revealed={revealed[current.itemId] === true}
            onReveal={reveal}
            onOpenInAdo={expectLeave}
            toggleTechnicalRef={toggleTechnicalRef}
            returnedAt={returned?.itemId === current.itemId ? returned.at : null}
          />
        ) : (
          <FixSlide fixes={fixes} />
        )}
      </div>
      {next && <Prefetch project={project} itemId={next.itemId} />}

      <div className="flex flex-none flex-wrap items-center gap-4 px-8 pb-[22px] pt-4 lg:px-16" data-testid="present-footer">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Previous slide"
          disabled={index === 0}
          onClick={() => {
            move(-1);
          }}
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <div className="flex gap-1.5" data-testid="present-dots">
          {Array.from({ length: slideCount }, (_, dot) => {
            // The closing slide is not an agenda item ("After the agenda"), so its dot is an end marker of its own.
            const closing = dot === items.length;
            return (
              <button
                key={`dot-${String(dot)}`}
                type="button"
                aria-label={closing ? FIX_LIST_HEADING : `Slide ${String(dot + 1)}`}
                aria-current={dot === index ? "step" : undefined}
                onClick={() => {
                  setIndex(dot);
                }}
                data-kind={closing ? "closing" : "item"}
                className={cn(
                  "h-1 transition-[background-color,width] duration-300",
                  closing
                    ? cn("ml-1.5 rounded-[1px]", dot === index ? "w-4 bg-[#E11837]" : "w-2.5 bg-transparent ring-1 ring-inset ring-[#8B8B94]")
                    : cn("rounded-full", dot === index ? "w-10 bg-[#E11837]" : dot < index ? "w-[22px] bg-zinc-600" : "w-[22px] bg-[#27272A]")
                )}
              />
            );
          })}
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Next slide"
          disabled={index === slideCount - 1}
          onClick={() => {
            move(1);
          }}
        >
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <span className="flex-1" />
        <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="present-hint">
          {presentFooterText({ onItem: current !== undefined, canReveal })}
        </span>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Opening it
// ---------------------------------------------------------------------------

/**
 * Present mode for an agenda batch (the shared draft or a run): its items as
 * slides, and the fix list (FIX_LIST_HEADING) from the run and the
 * top of the backlog. Mount only while open: the backlog read it needs is
 * the one the build step and the palette already share.
 */
export function AgendaPresent({
  project,
  batch,
  sessionDate,
  onClose,
}: {
  project: string;
  batch: GroomingBatch | null;
  sessionDate: string | null;
  onClose: () => void;
}) {
  const mayPresent = useCan(project, "present").allowed;
  // ADO backlog order; the Backlog page's sort menu never changes Present mode (E280, PI-SPEC-SW-1406).
  const backlog = useGroomingBacklog(project, "all", "rank");
  const items = useMemo(() => presentItems(batch), [batch]);
  const fixes = useMemo(() => presentFixes(batch, backlog.data?.items), [batch, backlog.data]);
  if (!mayPresent) return null;
  return <PresentMode project={project} sessionDate={sessionDate} items={items} fixes={fixes} onClose={onClose} />;
}

/** The search parameter that opens Present mode (`?present=1`), so a link can open it. */
export const PRESENT_PARAM = "present";

/** Present mode's open state, kept in the URL (`?present=1`). */
export function usePresentParam(): { open: boolean; openPresent: () => void; closePresent: () => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get(PRESENT_PARAM) === "1";
  const openPresent = useCallback(() => {
    setSearchParams(
      (params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set(PRESENT_PARAM, "1");
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);
  const closePresent = useCallback(() => {
    setSearchParams(
      (params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.delete(PRESENT_PARAM);
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);
  return { open, openPresent, closePresent };
}

/** True when a modal dialog is open over the page. */
function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

/**
 * P opens Present mode on the Session screen. Ignored while typing, with
 * Ctrl, ⌘ or Alt held, and while the palette or a dialog (Present mode
 * included) is open.
 */
export function usePresentShortcut(enabled: boolean, onPresent: () => void): void {
  const latest = useRef(onPresent);
  useEffect(() => {
    latest.current = onPresent;
  }, [onPresent]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "p" && event.key !== "P") return;
      if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target) || useCommandPaletteStore.getState().open || dialogOpen()) return;
      event.preventDefault();
      latest.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled]);
}

/** The Present button for the Session header and the run step's header. */
export function PresentButton({ onPresent, disabled, size }: { onPresent: () => void; disabled: boolean; size?: "sm" }) {
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      disabled={disabled}
      title={disabled ? "Add an item to the agenda first" : "Present the agenda (P)"}
      onClick={onPresent}
      data-testid="present-slot"
    >
      <Presentation aria-hidden="true" />
      Present
    </Button>
  );
}
